// src/components/ImagePickerModal.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify'; // Re-use your Toastify instance

// Re-use your API_BASE_URL
const API_BASE_URL = process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_API_URL_PRODUCTION
    : process.env.REACT_APP_API_URL;

const ImagePickerModal = ({ isOpen, onClose, onSelectImages, presentationTitle }) => {
    const [availableImages, setAvailableImages] = useState([]);
    const [imageCategories, setImageCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedImages, setSelectedImages] = useState([]); // URLs of images chosen from gallery
    
    // For direct upload within this modal
    const [newUploadForm, setNewUploadForm] = useState({
        name: '',
        description: '',
        tags: '',
        photo: null,
        imageCategories: [],
    });
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const fileInputRef = useRef(null);

    const fetchImages = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/images`);
            setAvailableImages(res.data || []);
        } catch (err) {
            console.error('Error fetching images for picker:', err);
            toast.error('Failed to load images for selection.');
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/image-categories`);
            const sortedCategories = res.data ? [...res.data].sort((a, b) => a.name.localeCompare(b.name)) : [];
            setImageCategories(sortedCategories);
        } catch (err) {
            console.error('Error fetching categories for picker:', err);
            toast.error('Failed to load categories.');
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchImages();
            fetchCategories();
            setSelectedImages([]); // Reset selections on open
            setNewUploadForm({ // Reset upload form on open
                name: '',
                description: '',
                tags: '',
                photo: null,
                imageCategories: [],
            });
        }
    }, [isOpen, fetchImages, fetchCategories]);

    const getOrCreatePresentationCategory = useCallback(async () => {
        try {
            const existingCategory = imageCategories.find(cat => cat.name.toLowerCase() === presentationTitle.toLowerCase());
            if (existingCategory) {
                return existingCategory._id;
            } else {
                const res = await axios.post(`${API_BASE_URL}/api/image-categories`, { name: presentationTitle });
                toast.success(`Category "${presentationTitle}" created!`);
                fetchCategories(); // Re-fetch to include the new category
                return res.data._id;
            }
        } catch (err) {
            console.error('Error getting or creating presentation category:', err);
            toast.error('Error with presentation category.');
            return null;
        }
    }, [imageCategories, presentationTitle, fetchCategories]);

    const handleNewUploadChange = (e) => {
        const { name, value, files } = e.target;
        setNewUploadForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
    };

    const handleNewImageUpload = async (e) => {
        e.preventDefault();
        if (!newUploadForm.photo) {
            toast.error('Please select a file to upload.');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const presentationCategoryId = await getOrCreatePresentationCategory();
            const categoriesToAssign = presentationCategoryId ? [presentationCategoryId] : [];

            // Generate unique name for the uploaded photo
            const existingImageNames = availableImages.map(img => img.name);
            let baseName = presentationTitle.replace(/\s+/g, '-').toLowerCase();
            let uniqueName = baseName;
            let counter = 1;
            while (existingImageNames.includes(uniqueName)) {
                uniqueName = `${baseName}-${counter}`;
                counter++;
            }

            const formData = new FormData();
            formData.append('name', uniqueName);
            formData.append('description', newUploadForm.description);
            formData.append('tags', JSON.stringify(newUploadForm.tags.split(',').map(t => t.trim())));
            formData.append('imageCategories', JSON.stringify(categoriesToAssign));
            formData.append('photo', newUploadForm.photo);

            const res = await axios.post(`${API_BASE_URL}/api/images`, formData, {
                onUploadProgress: (e) => {
                    setUploadProgress(Math.round((e.loaded * 100) / e.total));
                },
            });
            toast.success(`📤 "${uniqueName}" uploaded and added!`);
            fetchImages(); // Refresh available images
            setSelectedImages(prev => [...prev, res.data.url]); // Automatically select the newly uploaded image
            setNewUploadForm({ // Reset upload form
                name: '',
                description: '',
                tags: '',
                photo: null,
                imageCategories: [],
            });
            if (fileInputRef.current) fileInputRef.current.value = '';

        } catch (err) {
            console.error('Upload error:', err);
            toast.error('❌ Failed to upload new image.');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const toggleImageSelection = (imageUrl) => {
        setSelectedImages(prev => 
            prev.includes(imageUrl) ? prev.filter(url => url !== imageUrl) : [...prev, imageUrl]
        );
    };

    const handleAddSelected = () => {
        onSelectImages(selectedImages);
        onClose();
    };

    // Filter images based on selected category
    const filteredImages = availableImages.filter(img => 
        (!selectedCategory || img.imageCategories.some(cat => cat._id === selectedCategory))
    );

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content image-picker-modal" onClick={(e) => e.stopPropagation()}>

                {/* --- MOVED: UPLOAD FORM IS NOW AT THE TOP --- */}
                <h3>Upload New Image</h3>
                <form onSubmit={handleNewImageUpload} className="new-image-upload-form">
                    <input 
                        type="file" 
                        name="photo" 
                        accept="image/*" 
                        onChange={handleNewUploadChange} 
                        ref={fileInputRef}
                        required 
                    />
                    <input 
                        type="text" 
                        name="description" 
                        placeholder="Description (Optional)" 
                        value={newUploadForm.description} 
                        onChange={handleNewUploadChange} 
                    />
                    <input 
                        type="text" 
                        name="tags" 
                        placeholder="Tags (comma separated, Optional)" 
                        value={newUploadForm.tags} 
                        onChange={handleNewUploadChange} 
                    />
                    <button type="submit" disabled={isUploading}>
                        {isUploading ? `Uploading... (${uploadProgress}%)` : 'Upload & Add'}
                    </button>
                </form>
                {/* --- END MOVED SECTION --- */}
                
                <div className="image-picker-selected-count">
                    Selected: {selectedImages.length}
                </div>

                <div className="modal-actions">
                    <button onClick={handleAddSelected} disabled={selectedImages.length === 0}>
                        Add Selected ({selectedImages.length})
                    </button>
                    <button onClick={onClose}>Close</button>
                </div>

                <h2>Select from Gallery</h2>

                <div className="image-picker-controls">
                    <select 
                        value={selectedCategory} 
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="category-filter"
                    >
                        <option value="">All Categories</option>
                        {imageCategories.map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                    </select>
                </div>

                <div className="image-picker-grid">
                    {filteredImages.length > 0 ? (
                        filteredImages.map(img => (
                            <div 
                                key={img._id} 
                                className={`gallery-image-wrapper ${selectedImages.includes(img.url) ? 'selected' : ''}`}
                                onClick={() => toggleImageSelection(img.url)}
                            >
                                <img src={img.url} alt={img.name} />
                                <div className="checkbox-overlay">
                                    <input type="checkbox" readOnly checked={selectedImages.includes(img.url)} />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p>No images found in this category.</p>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ImagePickerModal;